import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendChatMessage } from '../../redux/slices/chat/SendChatMessage';
import { IoSend, IoClose } from 'react-icons/io5';
import { BiExpandAlt, BiCollapseAlt } from 'react-icons/bi';
import { IoChatbubbleEllipsesSharp } from 'react-icons/io5';
import { MdDeleteSweep } from 'react-icons/md';
import { fetchHistory } from '../../redux/slices/chat/FetchHistory';
import { clearChatHistory } from '../../redux/slices/chat/ClearChatHistory';

const ChatWidget = () => {
    const [message, setMessage] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const dispatch = useDispatch();
    const messageEndRef = useRef(null);

    // Get messages from Redux store
    const messages = useSelector(state => state?.chatHistory?.messages || []);
    const { isLoading, loadingMessage } = useSelector(state => state.chatSend);

    // Handle form submission
    // const [error, setError] = useState('');
    const [error, setError] = useState({ type: '', message: '' });

    // console.log(error)

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmedMessage = message.trim();
        if (!trimmedMessage) return;

        try {
            const res = await dispatch(sendChatMessage({ values: { item: trimmedMessage } }));
            // console.log('Full Response:', res);

            const status = res?.payload?.status;
            const requestState = res?.meta?.requestStatus;
            const detail = res?.payload?.data?.detail || '';

            // --- 1️⃣ CLIENT ERROR (400–499) ---
            if (status >= 400 && status < 500) {
                let msg = '⚠️ Please check your input and try again.';

                // Handle specific 404 case
                if (status === 404 || detail === 'Not Found') {
                    msg = '❌ Resource not found. Please check the your query.';
                }

                setError({
                    type: 'error',
                    message: msg,
                });
                setTimeout(() => setError({ type: '', message: '' }), 4000);
            }

            // --- 2️⃣ SERVER ERROR (500+) ---
            else if (status >= 500) {
                setError({
                    type: 'error',
                    message: '🚨 Server issue. Please try again later.',
                });
                setTimeout(() => setError({ type: '', message: '' }), 4000);
            }

            // --- 3️⃣ SUCCESS ---
            else if ((status === 200 || status === 201) && requestState === 'fulfilled') {
                setMessage('');
                dispatch(fetchHistory());
                setError({
                    type: 'success',
                    // message: '✅ Message sent successfully!',
                    message: '',
                });
                setTimeout(() => setError({ type: '', message: '' }), 3000);
            }

            // --- 4️⃣ REDUX REJECTED ---
            else if (requestState === 'rejected') {
                setError({
                    type: 'error',
                    message: '❌ Failed to process your request.',
                });
                setTimeout(() => setError({ type: '', message: '' }), 4000);
            }

            // --- 5️⃣ UNKNOWN / OTHER RESPONSE ---
            else {
                setError({
                    type: 'warning',
                    message: '🤖 Unexpected response. Please try again.',
                });
                setTimeout(() => setError({ type: '', message: '' }), 4000);
            }

        } catch (err) {
            console.error('Error sending message:', err);
            setError({
                type: 'error',
                message: '💥 Something went wrong. Please try again later.',
            });
            setTimeout(() => setError({ type: '', message: '' }), 4000);
        }
    };




    // Toggle chat window
    const toggleChat = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setExpanded(false);
        }
    };

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Fetch chat history on component mount
    useEffect(() => {
        dispatch(fetchHistory());
    }, [dispatch]);

    return (
        <>
            {/* Floating Chat Icon */}
            {!isOpen && (
                <div className="chat-icon-wrapper">
                    <button className="chat-icon-button" onClick={toggleChat} aria-label="Open chat">
                        <IoChatbubbleEllipsesSharp style={{ height: '25px', width: '25px' }} />
                    </button>
                </div>
            )}

            {/* Chat Window */}
            {isOpen && (
                <div className={`chat-window ${expanded ? 'expanded' : ''}`}>
                    {/* Chat Header */}
                    <div className="chat-header">
                        <div className="chat-info">
                            <div className="bot-avatar">
                                <IoChatbubbleEllipsesSharp />
                            </div>
                            <div className="chat-details">
                                <h3>AI Assistant</h3>
                                <span className="status-online">Online</span>
                            </div>
                        </div>
                        <div className="header-actions">
                            <button
                                className="action-btn"
                                onClick={() => {
                                    dispatch(clearChatHistory());
                                }}
                                title="Clear Chat"
                            >
                                <MdDeleteSweep />
                            </button>
                            <button
                                className="action-btn"
                                onClick={() => setExpanded(!expanded)}
                                title={expanded ? "Collapse" : "Expand"}
                            >
                                {expanded ? <BiCollapseAlt /> : <BiExpandAlt />}
                            </button>
                            <button
                                className="action-btn"
                                onClick={toggleChat}
                                title="Close"
                            >
                                <IoClose />
                            </button>
                        </div>
                    </div>

                    {/* Messages Container */}
                    <div className="messages-container">
                        {/* Empty State */}
                        {messages?.length === 0 && (
                            <div className="empty-state">
                                <div className="empty-icon">
                                    <IoChatbubbleEllipsesSharp />
                                </div>
                                <h4>Start a conversation</h4>
                                <p>Hi there! How can I help you today?</p>
                            </div>
                        )}

                        {/* Message Bubbles */}
                        {messages?.map((chat, index) => {
                            const timeStr = chat?.timestamp
                                ? new Date(chat.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                            const isBot = (chat.type || '').toLowerCase() === 'bot';

                            return (
                                <div
                                    key={index}
                                    className={`message-wrapper ${chat.type}`}
                                    style={{ position: 'relative', paddingBottom: 20 }}
                                >
                                    <div
                                        className="message-bubble"
                                        style={isBot ? { backgroundColor: '#d3d3d3', color: '#000' } : {}}
                                    >
                                        <p style={{ margin: 0 }}>{chat.message}</p>
                                    </div>

                                    {/* Timestamp */}
                                    <div
                                        className="timestamp-outside"
                                        style={{
                                            position: 'absolute',
                                            bottom: 2,
                                            left: isBot ? 0 : 'auto',
                                            right: isBot ? 'auto' : 0,
                                            fontSize: '0.72rem',
                                            color: '#888',
                                        }}
                                    >
                                        {timeStr}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Loading Indicator */}
                        {isLoading && (
                            <div className="typing-wrapper">
                                <div className="typing-indicator">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                                {loadingMessage && <span className="typing-text">{loadingMessage}</span>}
                            </div>
                        )}

                        {/* Auto-scroll anchor */}
                        <div ref={messageEndRef} />
                    </div>

                    {/* Error Message */}
                    {error?.message && (
                        <div
                            className={`chat-alert ${error.type === 'success'
                                ? 'chat-success'
                                : error.type === 'error'
                                    ? 'chat-error'
                                    : 'chat-warning'
                                }`}
                        >
                            {error.message}
                        </div>
                    )}


                    {/* Input Form */}
                    <form onSubmit={handleSubmit} className="input-container">
                        <input
                            type="text"
                            className="message-input"
                            value={message}
                            onChange={(e) => {
                                setMessage(e.target.value);
                                if (error?.message) setError({ type: '', message: '' }); // Clear error when user starts typing
                            }}
                            placeholder="Type a message..."
                            disabled={isLoading}
                        />
                        <button
                            type="submit"
                            className="send-btn"
                            disabled={isLoading || !message.trim()}
                            aria-label="Send"
                        >
                            <IoSend />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
};

export default ChatWidget;